import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { subscribeCommunities, toggleMembership, CATEGORIES } from "../../utils/communities";

export const ExploreCommunities = () => {
    const user = useSelector(selectUser);
    const [communities, setCommunities] = useState([]);
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("All");
    const [activeTag, setActiveTag] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = subscribeCommunities((list) => {
            setCommunities(list);
            setLoading(false);
        });
        return unsub;
    }, []);

    const trendingTags = useMemo(() => {
        const counts = {};
        communities.forEach((c) => (c.tags || []).forEach((t) => (counts[t] = (counts[t] || 0) + 1)));
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 12)
            .map(([tag]) => tag);
    }, [communities]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return communities.filter((c) => {
            if (category !== "All" && c.category !== category) return false;
            if (activeTag && !(c.tags || []).includes(activeTag)) return false;
            if (!q) return true;
            return (
                (c.name || "").toLowerCase().includes(q) ||
                (c.description || "").toLowerCase().includes(q) ||
                (c.slug || "").toLowerCase().includes(q)
            );
        });
    }, [communities, query, category, activeTag]);

    const handleJoin = async (c, e) => {
        e.preventDefault();
        e.stopPropagation();
        await toggleMembership(c.id, user);
    };

    return (
        <Page>
            <Top>
                <img
                    src="https://styles.redditmedia.com/t5_27gu25/styles/communityIcon_7sl9l62xhr6a1.png"
                    alt=""
                    hidden
                />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search communities..."
                />
                <h1>Explore</h1>
                <p>Find communities a name within something you like.</p>
            </Top>

            <Categories>
                {["All", ...CATEGORIES].map((cat) => (
                    <button key={cat} className={category === cat ? "active" : ""} onClick={() => setCategory(cat)}>
                        {cat}
                    </button>
                ))}
            </Categories>

            <Trending>
                <span className="label">Trending tags:</span>
                {trendingTags.length === 0 && <span className="light">No tags yet.</span>}
                {trendingTags.map((t) => (
                    <button key={t} className={activeTag === t ? "active" : ""} onClick={() => setActiveTag(activeTag === t ? "" : t)}>
                        {t}
                    </button>
                ))}
            </Trending>

            <Grid>
                {loading && <Note>Loading communities...</Note>}
                {!loading && filtered.length === 0 && <Note>No communities match your search.</Note>}

                {filtered.map((c) => {
                    const isMember = user && (c.members || []).includes(user.uid);
                    return (
                        <Card key={c.id}>
                            <div className="banner">{c.banner ? <img src={c.banner} alt="" /> : null}</div>
                            <div className="inner">
                                <AvatarRow>
                                    {c.avatar ? (
                                        <img src={c.avatar} alt={c.name} className="av" />
                                    ) : (
                                        <span className="av init">{(c.name || "C").charAt(0).toUpperCase()}</span>
                                    )}
                                    <Link to={`/c/${c.slug}`} className="visit">Visit</Link>
                                </AvatarRow>
                                <Link to={`/c/${c.slug}`} className="name">{c.name}</Link>
                                <div className="meta">
                                    <span>{c.members?.length || 0} Fellows</span>
                                    <span className="cat">{c.category}</span>
                                </div>
                                <p className="desc">{c.description || "No description yet."}</p>
                                {c.tags?.length > 0 && (
                                    <div className="tags">
                                        {c.tags.slice(0, 4).map((t) => (
                                            <span key={t}>#{t}</span>
                                        ))}
                                    </div>
                                )}
                                <button
                                    className={`join ${isMember ? "joined" : ""}`}
                                    onClick={(e) => handleJoin(c, e)}
                                >
                                    {isMember ? "Joined" : "Join"}
                                </button>
                            </div>
                        </Card>
                    );
                })}
            </Grid>
        </Page>
    );
};

export default ExploreCommunities;

const Page = styled.div`
    min-height: 100vh;
    background-color: var(--primary-base-cream);
    padding: 0 0 3rem;

    @media (max-width: 900px) {
        padding: 0 0.75rem 3rem;
    }
`;

const Top = styled.div`
    position: relative;
    padding: 4.5rem 1rem 4rem;
    text-align: center;
    background: radial-gradient(circle at 20% 30%, rgba(29,161,242,0.15), transparent 40%),
        radial-gradient(circle at 80% 70%, rgba(201,160,99,0.2), transparent 45%);

    input {
        position: absolute;
        top: 1.4rem;
        left: 50%;
        transform: translateX(-50%);
        width: min(420px, 90%);
        padding: 9px 16px;
        border-radius: 20px;
        border: 1px solid #e0d8cc;
        font-size: 14px;
        outline: none;
        box-shadow: 0 2px 10px rgba(10,20,40,0.06);
        &:focus { border-color: #1DA1F2; }
    }

    h1 { margin: 0 0 6px; font-size: 34px; color: #0a1428; letter-spacing: -0.5px; }
    p { margin: 0; font-size: 16px; color: #666; font-style: italic; }
`;

const Categories = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    max-width: 1000px;
    margin: 1.4rem auto 0.6rem;

    button {
        background: white;
        border: 1px solid rgba(201,160,99,0.35);
        color: #555;
        border-radius: 18px;
        padding: 6px 14px;
        font-size: 13px;
        cursor: pointer;
        font-weight: 600;
        &:hover { border-color: #1DA1F2; color: #1DA1F2; }
        &.active { background: #1DA1F2; color: white; border-color: #1DA1F2; }
    }
`;

const Trending = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    align-items: center;
    max-width: 1000px;
    margin: 0 auto 1.6rem;

    .label { font-size: 12px; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: 0.4px; }
    .light { font-size: 13px; color: #aaa; }
    button {
        background: #fff8ec;
        border: 1px dashed #c9a063;
        color: #6b4e2a;
        border-radius: 14px;
        padding: 4px 12px;
        font-size: 12px;
        cursor: pointer;
        &:hover { background: #fff1d9; }
        &.active { background: #c9a063; color: white; border: 1px solid #c9a063; }
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 1rem;
    max-width: 1080px;
    margin: 0 auto;
`;

const Card = styled.div`
    background: white;
    border: 1px solid rgba(201,160,99,0.25);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(10,20,40,0.06);
    display: flex;
    flex-direction: column;

    .banner {
        height: 70px;
        background: linear-gradient(135deg,#1DA1F2,#0d8ecf);
        img { width: 100%; height: 100%; object-fit: cover; }
    }

    .inner { padding: 0 1rem 1rem; display: flex; flex-direction: column; flex: 1; }

    .av {
        width: 60px; height: 60px;
        border-radius: 50%;
        border: 3px solid white;
        margin-top: -30px;
        object-fit: cover;
        background: linear-gradient(135deg,#c9a063,#e8c47d);
        color: white;
        &.init { display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: 700; }
    }

    .name { font-size: 17px; font-weight: 700; color: #0a1428; text-decoration: none; margin-top: 6px; &:hover { color: #1DA1F2; } }

    .meta { display: flex; gap: 8px; align-items: center; font-size: 12px; color: #888; margin-top: 4px;
        .cat { background: #e8f5fe; color: #1DA1F2; font-weight: 700; padding: 1px 8px; border-radius: 10px; font-size: 11px; }
    }

    .desc { font-size: 13px; color: #555; line-height: 1.4; flex: 1; margin: 8px 0; }

    .tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px;
        span { font-size: 11px; color: #1DA1F2; background: #e8f5fe; border-radius: 10px; padding: 2px 8px; }
    }

    .join {
        width: 100%;
        border: 1.5px solid #1DA1F2;
        color: #1DA1F2;
        background: transparent;
        border-radius: 18px;
        padding: 7px 0;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
        &:hover { background: #e8f5fe; }
        &.joined { background: #1DA1F2; color: white; border-color: #1DA1F2; }
    }
`;

const AvatarRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;

    .visit {
        margin-bottom: 8px;
        font-size: 12px;
        font-weight: 700;
        color: #1DA1F2;
        text-decoration: none;
        border: 1px solid #1DA1F2;
        padding: 4px 12px;
        border-radius: 14px;
        &:hover { background: #e8f5fe; }
    }
`;

const Note = styled.div`
    grid-column: 1 / -1;
    text-align: center;
    color: #999;
    padding: 3rem 1rem;
    font-size: 15px;
`;